<template>
  <div>
    <header class="mb-6">
      <h1 class="text-2xl font-bold">Score a new sample</h1>
    </header>

    <div class="grid gap-6 lg:grid-cols-2">
      <!-- form -->
      <div class="card p-5">
        <h2 class="font-bold mb-4">New measurement</h2>

        <label class="label">Patient</label>
        <select v-model="patientUuid" class="input mb-4">
          <option :value="null" disabled>Select a patient…</option>
          <option value="__new__">➕ New patient…</option>
          <option v-for="p in patients" :key="p.uuid" :value="p.uuid">{{ p.label }}</option>
        </select>

        <!-- new patient details -->
        <div v-if="patientUuid === '__new__'" class="rounded-xl bg-gray-50 p-3 mb-4 space-y-3">
          <div>
            <label class="label mb-0.5">External patient ID</label>
            <input v-model="newPatient.external_id" class="input" type="text" placeholder="e.g. P-1024" />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="label mb-0.5">First name</label>
              <input v-model="newPatient.first_name" class="input" type="text" />
            </div>
            <div>
              <label class="label mb-0.5">Last name</label>
              <input v-model="newPatient.last_name" class="input" type="text" />
            </div>
          </div>
        </div>

        <label class="label">Device</label>
        <select v-model="deviceUuid" class="input mb-4">
          <option :value="null" disabled>Select a device…</option>
          <option v-for="d in devices" :key="d.uuid" :value="d.uuid">{{ d.label }}</option>
        </select>

        <label class="label">Title</label>
        <input v-model="title" class="input mb-5" type="text" placeholder="Measurement title" />

        <div class="space-y-3">
          <div v-for="f in fileFields" :key="f.key">
            <label class="label mb-0.5">{{ f.label }}</label>
            <input type="file" :accept="f.accept" class="block w-full text-sm"
                   @change="pick(f.key, $event)" />
            <span v-if="files[f.key]" class="text-xs text-ink-soft">
              {{ files[f.key].name }} · {{ mb(files[f.key].size) }} MB
            </span>
          </div>
        </div>

        <button class="btn btn-primary mt-5" :disabled="!ready || loading" @click="submit">
          <font-awesome-icon v-if="loading" icon="circle-notch" spin class="mr-2" />
          <font-awesome-icon v-else icon="upload" class="mr-2" />
          {{ loading ? 'Scoring…' : 'Score sample' }}
        </button>

        <p v-if="loading" class="mt-3 text-xs text-ink-muted">
          The MATLAB engine typically takes 30–90 s per sample.
        </p>
        <p v-if="error" class="mt-4 text-sm text-red-600">
          <font-awesome-icon icon="exclamation-triangle" class="mr-1" />{{ error }}
        </p>
      </div>

      <!-- info panel -->
      <div class="card p-5 flex items-center justify-center text-ink-muted">
        <div class="text-center max-w-xs">
          <font-awesome-icon icon="vials" class="text-3xl mb-3 text-brand-600" />
          <p class="text-sm">
            On success you'll be taken to the full VPA report: total &amp; free VPA (mg/L),
            plus seizure- and side-effect probabilities.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api } from 'helpers/api'

export default {
  data() {
    return {
      patients: [],
      devices: [],
      patientUuid: null,
      deviceUuid: null,
      newPatient: { external_id: '', first_name: '', last_name: '' },
      title: 'New measurement',
      files: { positive_raw: null, negative_raw: null, positive_co2: null, negative_co2: null },
      fileFields: [
        { key: 'positive_raw', label: 'Positive mode (.RAW)', accept: '.RAW,.raw' },
        { key: 'negative_raw', label: 'Negative mode (.RAW)', accept: '.RAW,.raw' },
        { key: 'positive_co2', label: 'Positive CO₂ (Exhalion .txt)', accept: '.txt' },
        { key: 'negative_co2', label: 'Negative CO₂ (Exhalion .txt)', accept: '.txt' },
      ],
      loading: false,
      error: null,
    }
  },
  computed: {
    patientReady() {
      if (this.patientUuid === '__new__') {
        const p = this.newPatient
        return !!(p.external_id && p.first_name && p.last_name)
      }
      return !!this.patientUuid
    },
    ready() {
      return (
        this.patientReady && this.deviceUuid &&
        this.files.positive_raw && this.files.negative_raw &&
        this.files.positive_co2 && this.files.negative_co2
      )
    },
  },
  async created() {
    try {
      const opts = await api.scoringOptions()
      this.patients = opts.patients || []
      this.devices = opts.devices || []
    } catch (e) {
      this.error = 'Could not load patients/devices.'
    }
  },
  methods: {
    pick(key, e) {
      this.files[key] = e.target.files && e.target.files[0] ? e.target.files[0] : null
    },
    mb(bytes) {
      return (bytes / 1048576).toFixed(1)
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async submit() {
      this.loading = true
      this.error = null
      try {
        // Kicks off async scoring; returns a 'processing' measurement immediately.
        const res = await api.scoreMeasurement({
          patientUuid: this.patientUuid,
          newPatient: this.patientUuid === '__new__' ? this.newPatient : null,
          deviceUuid: this.deviceUuid,
          title: this.title,
          ...this.files,
        })
        // Poll until the MATLAB engine finishes (each poll is a fast request, so
        // no upstream-proxy timeout). Give it up to ~10 minutes.
        for (let i = 0; i < 200; i++) {
          await this.sleep(3000)
          const m = await api.getMeasurement(res.uuid)
          if (m.report_status && m.report_status !== 'processing') {
            if (m.report_status === 'error') {
              this.error = m.message || m.text || 'Scoring failed'
              this.loading = false
              return
            }
            this.$router.push({ name: 'MeasurementDetail', params: { uuid: res.uuid } })
            return
          }
        }
        this.error = 'Scoring is taking longer than expected — check Measurements shortly.'
      } catch (e) {
        const d = e.response && e.response.data
        this.error = (d && d.message) || e.message || 'Scoring failed'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
